// Sblocco biometrico all'apertura dell'app nativa. Le librerie Capacitor si importano in
// modo dinamico, solo dentro le funzioni (che si chiamano solo nel ramo `isNativo()`),
// così il modulo resta importabile nel bundle web e nei test.
const CHIAVE = "biometria";
// Deciso dal server a ogni accesso (chi amministra un'azienda): `biometria_obbligatoria`.
const CHIAVE_OBBLIGO = "biometria_obbligatoria";
// La proposta «Vuoi usare Face ID?» dopo il primo accesso si mostra una volta sola per telefono.
const CHIAVE_PROPOSTA = "biometria_proposta";

// Rientro dal background: lo sblocco si richiede dopo questa pausa.
export const RIVERIFICA_DOPO_MS = 5 * 60 * 1000;

/** Pura: lo sblocco serve solo su nativo, con un token locale, se attivato dall'utente o obbligatorio. */
export function serveBiometria({ nativo, attiva, obbligatoria = false, tokenPresente }) {
  return Boolean(nativo && tokenPresente && (attiva || obbligatoria));
}

/** Pura: `true` se l'app è rimasta in background abbastanza da richiedere lo sblocco. */
export function deveRiverificare({ inBackgroundDa, ora }) {
  return inBackgroundDa != null && ora - inBackgroundDa >= RIVERIFICA_DOPO_MS;
}

/** Pura: la proposta si mostra se il telefono può sbloccare, la biometria è spenta e non l'ha già vista. */
export function propostaDaMostrare({ disponibile, giaVista, attiva }) {
  return Boolean(disponibile && !giaVista && !attiva);
}

async function leggi(chiave) {
  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: chiave });
  return value === "1";
}

async function scrivi(chiave, valore) {
  const { Preferences } = await import("@capacitor/preferences");
  await Preferences.set({ key: chiave, value: valore ? "1" : "0" });
}

export const obbligoBiometria = () => leggi(CHIAVE_OBBLIGO);

/** Salva l'obbligo letto da una risposta di accesso (login, verifica, check_device). */
export const registraObbligoBiometria = (risposta) => scrivi(CHIAVE_OBBLIGO, risposta?.biometria_obbligatoria === true);

export const propostaGiaVista = () => leggi(CHIAVE_PROPOSTA);
export const segnaPropostaVista = () => scrivi(CHIAVE_PROPOSTA, true);

export const biometriaAttiva = () => leggi(CHIAVE);
export const impostaBiometria = (attiva) => scrivi(CHIAVE, attiva);

/**
 * `true` se il telefono può verificare chi lo usa: Face ID / impronta registrati, oppure almeno
 * il codice di sblocco (lo sblocco ammette il codice come ripiego).
 */
export async function biometriaDisponibile() {
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const esito = await BiometricAuth.checkBiometry();
    return esito.isAvailable === true || esito.deviceIsSecure === true;
  } catch {
    return false;
  }
}

/**
 * Pura: `true` = si prosegue col flusso normale. Se la biometria non è disponibile non può
 * mai riuscire (non registrata, assente): si prosegue e il chiamante azzera l'impostazione.
 */
export function esitoSblocco({ disponibile, autenticato }) {
  return !disponibile || autenticato;
}

/**
 * `true` = proseguire (autenticato, oppure biometria non disponibile e impostazione azzerata);
 * `false` solo se l'autenticazione è rifiutata o annullata. Il codice del dispositivo è
 * ammesso come ripiego.
 */
export async function sbloccaConBiometria() {
  const disponibile = await biometriaDisponibile();
  if (!disponibile) {
    await impostaBiometria(false);
    return esitoSblocco({ disponibile, autenticato: false });
  }
  let autenticato = false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    await BiometricAuth.authenticate({ reason: "Sblocca TodoE", allowDeviceCredential: true });
    autenticato = true;
  } catch {
    autenticato = false;
  }
  return esitoSblocco({ disponibile, autenticato });
}

/**
 * Gestore di `appStateChange` (@capacitor/app): al rientro dopo RIVERIFICA_DOPO_MS chiede lo
 * sblocco, se serve; se l'utente lo rifiuta, esce. Mentre la finestra di Face ID è aperta l'app
 * risulta in pausa: quegli eventi si ignorano, così lo sblocco non si ripete.
 */
export function creaControlloRipresa({ ora = () => Date.now(), serve, sblocca, esci }) {
  let inBackgroundDa = null;
  let inCorso = false;
  return async ({ isActive }) => {
    if (inCorso) return;
    if (!isActive) {
      inBackgroundDa = ora();
      return;
    }
    const da = inBackgroundDa;
    inBackgroundDa = null;
    if (!deveRiverificare({ inBackgroundDa: da, ora: ora() })) return;
    inCorso = true;
    try {
      if ((await serve()) && !(await sblocca())) await esci();
    } finally {
      inCorso = false;
    }
  };
}
